import posthog from "posthog-js";
import { shouldEnableAnalytics } from "./shouldEnableAnalytics";
import { sanitizeAnalyticsProperties } from "@/daily/challengeRoute";
import { resolveDistributionChannel } from "./distributionChannel";

/**
 * The only file in this project allowed to import `posthog-js` - every
 * other module (scenes, `gameAnalytics.ts`) only ever calls `capture()`
 * below, so PostHog itself could be swapped out without touching
 * anything else. Mirrors `supabaseClient.ts`'s lazy-singleton shape on
 * purpose: importing this module never talks to the network by itself,
 * `init()` is idempotent, and every entry point fails soft (never throws
 * into gameplay) on a misconfigured or offline environment.
 */

const POSTHOG_EU_HOST = "https://eu.i.posthog.com";

/** See `distributionChannel.ts`'s own doc - resolved once from the build-time env var, defaulting to `"direct"`. */
const DISTRIBUTION_CHANNEL = resolveDistributionChannel(import.meta.env.VITE_DISTRIBUTION_CHANNEL as string | undefined);

let didInit = false;
let enabled = false;

/**
 * Called once at app startup (see `main.ts`). Decides whether analytics
 * is allowed to run at all - production build, a real project key, and
 * the exact production hostname (see `shouldEnableAnalytics`) - and, only
 * if so, initializes PostHog with a deliberately minimal, privacy-first
 * configuration: no autocapture, no session recording, no heatmaps/
 * dead clicks/exception autocapture/web vitals, no cookies
 * (`cookieless_mode: 'always'`), and `person_profiles: 'never'` (this
 * project never calls `identify()` - every event is anonymous, scoped to
 * a per-run `run_id`, never a persistent player identity). Safe to call
 * more than once - every call after the first is a no-op.
 */
export function initAnalytics(): void {
  if (didInit) return;
  didInit = true;

  try {
    const hostname = typeof window !== "undefined" ? window.location.hostname : null;
    enabled = shouldEnableAnalytics({
      isProd: import.meta.env.PROD,
      hasKey: Boolean(import.meta.env.VITE_POSTHOG_KEY),
      hostname,
    });
    if (!enabled) return;

    posthog.init(import.meta.env.VITE_POSTHOG_KEY as string, {
      api_host: POSTHOG_EU_HOST,
      autocapture: false,
      capture_pageview: true,
      capture_pageleave: true,
      disable_session_recording: true,
      // Explicit `false` for every automatic capture feature posthog-js
      // otherwise falls back to the PostHog project's REMOTE settings for
      // (each of these defaults to `undefined` in the pinned SDK, meaning
      // "use whatever the project dashboard says") - so a dashboard toggle
      // can never silently start sending heatmap/dead-click/exception/
      // web-vitals events from this app. Client `false` always wins over
      // the remote flag for all four (`capture_performance: false` is the
      // web-vitals switch; its other use, replay network timing, is moot
      // with session recording disabled above).
      capture_heatmaps: false,
      capture_dead_clicks: false,
      capture_exceptions: false,
      capture_performance: false,
      person_profiles: "never",
      cookieless_mode: "always",
      defaults: "2026-05-30",
      // Critical privacy requirement (PR10): a "Beat My Score" share link
      // is served at `/c/<random-public-id>` - that id must NEVER reach
      // PostHog, in `$current_url`, `$pathname`, `$referrer`, or any other
      // property PostHog's own pageview/pageleave/autocapture/exception
      // machinery might attach a URL to. `sanitize_properties` is called
      // by posthog-js itself, INSIDE its capture pipeline, for every event
      // (including the automatic pageview this same `init()` call turns
      // on) before anything is queued for network send - configuring it
      // here means the rewrite is in place before the very first capture
      // this call can ever trigger. `sanitizeAnalyticsProperties` rewrites
      // every string property's `/c/<id>` segment to the fixed template
      // `/c/:token`, whatever key it's under - see that function's doc for
      // why this is deliberately a blanket transform rather than a fixed
      // list of property names.
      sanitize_properties: (properties) => sanitizeAnalyticsProperties(properties as Record<string, unknown>),
    });

    // PR33: registers `distribution_channel` as a PostHog super property,
    // so every event this session sends from here on - this project's
    // own typed events (via `capture()` below) and PostHog's own
    // automatic pageview/pageleave alike - carries it without threading
    // it through every call site. Never inferred from referrer; see
    // `distributionChannel.ts`'s own doc.
    posthog.register({ distribution_channel: DISTRIBUTION_CHANNEL });
  } catch {
    // Analytics must never be able to break the app, even at startup.
    enabled = false;
  }
}

/**
 * Sends one already-shaped event. Every caller in this project goes
 * through `trackEvent()` (see `track.ts`), never this directly - this is
 * only the safe-no-op transport. A no-op whenever analytics isn't
 * enabled (including every non-production environment and every test
 * run, where `window` doesn't exist at all), and any PostHog/network
 * failure is swallowed here so it can never propagate into gameplay.
 */
export function capture(eventName: string, properties: Record<string, unknown>): void {
  if (!enabled) return;
  try {
    // Belt-and-suspenders alongside `init()`'s own `sanitize_properties`
    // (which already covers this call too, since it runs inside
    // posthog-js's capture pipeline): never let a challenge URL/token
    // reach `posthog.capture()` even in a property this project's own
    // event schema didn't anticipate.
    posthog.capture(eventName, sanitizeAnalyticsProperties(properties));
  } catch {
    // Never let an analytics failure affect gameplay.
  }
}

/** Whether analytics actually initialized this session - exposed for tests/diagnostics only, never branched on by gameplay code. */
export function isAnalyticsEnabled(): boolean {
  return enabled;
}

/** Test-only: resets the module's lazy singleton state so each test starts fresh, same pattern as `supabaseClient.ts`'s `__resetSupabaseClientForTests`. */
export function __resetAnalyticsClientForTests(): void {
  didInit = false;
  enabled = false;
}
